import {
  Box,
  Button,
  Flex,
  Form,
  InlineStack,
  PanelSection,
  Select,
  Text,
  TextArea,
  TextField,
} from '@customerio/pluma-components/react';
import type { SelectOptionItem } from '@customerio/pluma-components/react';
import { newTheme } from '@customerio/pluma-components/css';
import { Controller, type UseZodFormReturn } from '@/components/form';
import { COUNTRIES } from '@/utils/countries';
import {
  HEARD_ABOUT_US_OPTIONS,
  INCUBATOR_VC_OPTIONS,
  STARTUP_FUNDING_OPTIONS,
} from '../utils/startup-incubator-options';
import { flagEmoji, PHONE_COUNTRY_OPTIONS } from '../utils/phone-country-codes';
import { useStartupForm, type StartupFormValues } from './use-startup-form';
import * as styles from './signup-startup-form.css';

const COMPANY_LOCATION_OPTIONS = COUNTRIES.map((country) => ({ value: country, label: country }));

// The select popovers portal outside the funnel's forced-light wrapper, so
// they'd inherit the visitor's ambient (possibly dark) theme against a light
// form without this.
const POPOVER_THEME_CLASS = newTheme.className;

type StartupForm = ReturnType<typeof useStartupForm>;

// The renderers receive the generic option item; the dial code lives in the
// full country option, looked up by value.
const PHONE_OPTION_BY_VALUE = new Map(PHONE_COUNTRY_OPTIONS.map((option) => [option.value, option]));

type PhoneCountryValueProps = {
  option?: SelectOptionItem;
};

function PhoneCountryValue({ option }: PhoneCountryValueProps) {
  const country = option == null ? undefined : PHONE_OPTION_BY_VALUE.get(option.value);
  if (country == null) {
    return null;
  }
  return (
    <InlineStack alignBlock="center" gap="075">
      <span aria-hidden="true">{flagEmoji(country.value)}</span>
      <Text size="sm">{country.dialCode}</Text>
    </InlineStack>
  );
}

type PhoneCountryOptionRowProps = {
  option: SelectOptionItem;
};

function PhoneCountryOptionRow({ option }: PhoneCountryOptionRowProps) {
  const country = PHONE_OPTION_BY_VALUE.get(option.value);
  if (country == null) {
    return null;
  }
  return (
    <InlineStack alignBlock="center" gap="100">
      <span aria-hidden="true">{flagEmoji(country.value)}</span>
      <Text size="sm">{country.label}</Text>
      <Text size="sm" color="subtle">
        {country.dialCode}
      </Text>
    </InlineStack>
  );
}

type StartupSelectName = Extract<
  keyof StartupFormValues,
  'companyLocation' | 'startupFunding' | 'incubatorVc' | 'heardAboutUs'
>;

type StartupSelectProps = {
  form: UseZodFormReturn<StartupFormValues>;
  onPatch: StartupForm['patchFormValues'];
  name: StartupSelectName;
  label: string;
  options: Array<{ value: string; label: string }>;
  isSearchable?: boolean;
  testId: string;
};

function StartupSelect({ form, onPatch, name, label, options, isSearchable, testId }: StartupSelectProps) {
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field, fieldState }) => (
        <Select
          flex={1}
          label={label}
          options={options}
          value={field.value}
          onChange={(value) => {
            const next = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
            field.onChange(next);
            onPatch({ [name]: next });
          }}
          onBlur={field.onBlur}
          isSearchable={isSearchable}
          withVirtualizer={isSearchable}
          isInvalid={fieldState.error != null}
          error={fieldState.error?.message}
          unsafe_popoverClassName={POPOVER_THEME_CLASS}
          data-testid={testId}
        />
      )}
    />
  );
}

type PhoneFieldProps = Pick<StartupForm, 'form' | 'registerStartupField' | 'patchFormValues'>;

/** The country select and number input fused into what reads as one control. */
function PhoneField({ form, registerStartupField, patchFormValues }: PhoneFieldProps) {
  return (
    <Box mb="200">
      <Text text="product-label" color="subtle" mb="075" display="block">
        Phone
      </Text>
      <Box className={styles.phoneFieldStackContainer}>
        <Flex gap="0" className={styles.phoneFieldStackInner}>
          <Box className={`${styles.phoneFieldSlot} ${styles.phoneCountrySlot}`}>
            <Controller
              name="phoneCountry"
              control={form.control}
              render={({ field }) => (
                <Select
                  ariaLabel="Country code"
                  options={PHONE_COUNTRY_OPTIONS}
                  value={field.value}
                  onChange={(value) => {
                    const next = Array.isArray(value) ? (value[0] ?? 'US') : (value ?? 'US');
                    field.onChange(next);
                    patchFormValues({ phoneCountry: next });
                    // The number's validity depends on the country's rules.
                    void form.trigger('phone');
                  }}
                  isSearchable
                  withVirtualizer
                  valueComponent={PhoneCountryValue}
                  optionComponent={PhoneCountryOptionRow}
                  unsafe_popoverSize={{ minWidth: 320 }}
                  unsafe_popoverClassName={POPOVER_THEME_CLASS}
                  data-testid="signup-startup-phone-country"
                />
              )}
            />
          </Box>
          <Box flex={1} className={styles.phoneFieldSlot}>
            <TextField
              {...registerStartupField('phone')}
              ariaLabel="Phone number"
              type="tel"
              placeholder="e.g. 555-123-4567"
              isInvalid={form.formState.errors.phone != null}
              error={form.formState.errors.phone?.message}
            />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}

export function SignupStartupForm() {
  const startup = useStartupForm();
  const { form, registerStartupField, patchFormValues } = startup;

  return (
    <PanelSection data-testid="signup-startup-form">
      <Form onSubmit={startup.onSubmit}>
        <Box mb="200">
          <Text text="product-label" color="subtle" mb="075" display="block">
            Name
          </Text>
          <Flex gap="100">
            <TextField
              {...registerStartupField('firstName')}
              flex={1}
              ariaLabel="First name"
              placeholder="First name"
            />
            <TextField {...registerStartupField('lastName')} flex={1} ariaLabel="Last name" placeholder="Last name" />
          </Flex>
        </Box>

        <Box mb="200">
          <TextField {...registerStartupField('email')} label="Company email" shouldAllowAutofill={true} />
        </Box>

        <PhoneField form={form} registerStartupField={registerStartupField} patchFormValues={patchFormValues} />

        <Flex mb="200" gap="100">
          <TextField {...registerStartupField('companyName')} flex={1} label="Startup name" placeholder="Acme, Inc." />
          <StartupSelect
            form={form}
            onPatch={patchFormValues}
            name="companyLocation"
            label="Company location"
            options={COMPANY_LOCATION_OPTIONS}
            isSearchable
            testId="signup-startup-company-location"
          />
        </Flex>

        <Box mb="200">
          <TextField
            {...registerStartupField('companyUrl')}
            label="Startup website"
            placeholder="https://example.com"
          />
        </Box>

        <Flex mb="200" gap="100">
          {/*
            A TextField, not Pluma's NumberField: with `withControls={false}` —
            which is what this field wants — NumberField *is* a TextField with
            `inputMode="decimal"`, so this renders the same control while keeping
            the flow's string-with-`''`-unset convention. Its numeric contract
            moved to the schema, which is stronger: what ships as `company_size`
            is now validated rather than merely filtered by the input.
          */}
          <Box flex={1}>
            <TextField
              {...registerStartupField('startupSize')}
              label="Startup size"
              placeholder="e.g. 23"
              inputMode="decimal"
              data-testid="signup-startup-size"
            />
          </Box>
          <StartupSelect
            form={form}
            onPatch={patchFormValues}
            name="startupFunding"
            label="Startup funding"
            options={STARTUP_FUNDING_OPTIONS}
            testId="signup-startup-funding"
          />
        </Flex>

        {startup.isIncubatorVcHidden ? null : (
          <Box mb="200">
            <StartupSelect
              form={form}
              onPatch={patchFormValues}
              name="incubatorVc"
              label="Select your incubator, VC firm, or startup partner"
              options={INCUBATOR_VC_OPTIONS}
              isSearchable
              testId="signup-startup-incubator-vc"
            />
          </Box>
        )}

        <Box mb="200">
          <StartupSelect
            form={form}
            onPatch={patchFormValues}
            name="heardAboutUs"
            label="Where did you hear about this?"
            options={HEARD_ABOUT_US_OPTIONS}
            testId="signup-startup-heard-about-us"
          />
        </Box>

        <Box mb="200">
          <TextArea
            {...registerStartupField('businessDescription')}
            label="Tell us a little about your business"
            placeholder="Anything you want to share with our application review team?"
            data-testid="signup-startup-business-description"
          />
        </Box>

        <Flex justifyContent="flex-end" mt="200">
          <Button
            type="submit"
            variant="primary"
            icon="chevron-right"
            iconPosition="trailing"
            isDisabled={startup.isSubmitDisabled}
            isLoading={startup.isSubmitting}
          >
            Next: Verify email
          </Button>
        </Flex>
      </Form>
    </PanelSection>
  );
}
